import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCartStore } from "./cartStore";
import { findVariant, filterValidCartItems, getCartItemQuantity } from "./shopVariants";
import { getGatedMedications, type IntakeStatus } from "./intakeGating";
import type { ShopPriceVariant, ShopProduct } from "./types";

export type ShopStep = "catalog" | "cart" | "checkout-questions" | "checkout-payment" | "confirmation";

export interface UseShopBlockLogicOptions {
  products: ShopProduct[];
  initialStep?: ShopStep;
}

export interface PaymentCartRow {
  quantity: number;
  product: ShopProduct;
  variant: ShopPriceVariant;
  /** True when this item failed intake gating and must be excluded from the charge. */
  ineligible: boolean;
}

export const useShopBlockLogic = ({ products, initialStep = "catalog" }: UseShopBlockLogicOptions) => {
  const { t } = useTranslation("shop");
  const [step, setStep] = useState<ShopStep>(initialStep);
  const [learnMoreProduct, setLearnMoreProduct] = useState<ShopProduct | null>(null);
  // Snapshotted when entering the payment step and held through confirmation, since PaymentSummary
  // clears the live cart on successful checkout — reading the live cart there would go blank the
  // instant the order confirms, right when the confirmation screen needs to render.
  const [paymentCartRows, setPaymentCartRows] = useState<PaymentCartRow[]>([]);
  // Per-product intake outcome for the current checkout attempt only — intentionally NOT persisted
  // (unlike checkoutProgress.answers), since a prior purchase's completed intake must never be
  // reused: every purchase attempt requires a fresh submission, even for the same product.
  const [intakeStatusByProductId, setIntakeStatusByProductId] = useState<Record<string, IntakeStatus>>({});

  const items = useCartStore((state) => state.items);
  const checkoutProgress = useCartStore((state) => state.checkoutProgress);
  const enterCheckoutProgress = useCartStore((state) => state.enterCheckout);
  const setCheckoutMedIndex = useCartStore((state) => state.setCheckoutMedIndex);
  const setCheckoutQuestionIndex = useCartStore((state) => state.setCheckoutQuestionIndex);
  const setCheckoutAnswers = useCartStore((state) => state.setCheckoutAnswers);
  const completeIntakeProgress = useCartStore((state) => state.completeIntake);
  const dismissResumeBanner = useCartStore((state) => state.dismissResumeBanner);

  const productsById = useMemo(() => new Map(products.map((p) => [p.stripeProductId, p])), [products]);

  // The distinct products in the cart, in cart order, regardless of intake gating — used to resolve
  // cart items back to their product for display/pricing purposes elsewhere. Filtered through
  // filterValidCartItems first since a cart item can outlive its product (e.g. removed from the
  // Shop block's config after being added to the cart).
  const cartProducts = useMemo(
    () =>
      filterValidCartItems(items, products).reduce<ShopProduct[]>((resolved, item) => {
        const product = productsById.get(item.stripeProductId);
        if (product) resolved.push(product);
        return resolved;
      }, []),
    [items, products, productsById]
  );

  // Only cart products with an associated form require a step in the intake wizard — a product
  // with no formId proceeds straight through checkout with no question step at all. One full
  // question set is answered per gated medication.
  const cartMedications = useMemo(() => getGatedMedications(cartProducts), [cartProducts]);

  // TODO(native-forms): call this from the intake form step's real submission callback once
  // available — "passed" on a successful submission, "failed" on rejection.
  const setIntakeStatus = (stripeProductId: string, status: IntakeStatus) =>
    setIntakeStatusByProductId((prev) => ({ ...prev, [stripeProductId]: status }));

  const goToShop = () => setStep("catalog");
  const goToCart = () => setStep("cart");

  // v1 ships with no form-gated products (FormAssociationPicker was removed from the admin editor),
  // so cartMedications is always empty and the branch below setting step to "checkout-questions" is
  // structurally unreachable today. Left in place for the forms fast follow — note that reaching it
  // isn't sufficient on its own: ShopBlock.tsx currently has no render case for "checkout-questions"
  // either, so that also needs restoring alongside whatever makes this branch reachable again.
  const enterCheckout = () => {
    // No gated items in this cart: nothing for the intake wizard to do, so skip straight to
    // payment — mirrors the "mixed cart" requirement's degenerate all-non-gated case. Also skip
    // marking intakeStarted in this case: it drives the resume banner, and there is no intake to
    // resume, so a cart with only non-gated products must never show it.
    if (cartMedications.length === 0) {
      snapshotPaymentCartRows();
      setStep("checkout-payment");
      return;
    }

    // Fresh per attempt — see intakeStatusByProductId's own note above.
    setIntakeStatusByProductId(Object.fromEntries(cartMedications.map((p) => [p.stripeProductId, "pending"])));
    enterCheckoutProgress();
    setStep("checkout-questions");
  };

  const snapshotPaymentCartRows = () => {
    const rows: PaymentCartRow[] = [];
    for (const item of items) {
      const product = productsById.get(item.stripeProductId);
      const variant = product && findVariant(product, item.stripePriceId);
      if (!product || !variant) continue;
      // A gated product with no recorded status yet (still "pending") is treated as eligible here,
      // since payment is only reached after the checkout-questions step resolves every gated item
      // to passed/failed. TODO(native-forms): "failed" should be set by the real intake-form
      // submission outcome once the form step is wired up.
      const ineligible = intakeStatusByProductId[product.stripeProductId] === "failed";
      rows.push({ quantity: getCartItemQuantity(item, product), product, variant, ineligible });
    }
    setPaymentCartRows(rows);
  };

  const completeIntake = () => {
    snapshotPaymentCartRows();
    setStep("checkout-payment");
    completeIntakeProgress();
  };

  const goToPayment = () => {
    snapshotPaymentCartRows();
    setStep("checkout-payment");
  };
  const goToConfirmation = () => setStep("confirmation");

  // intakeStatusByProductId is intentionally in-memory only (see its own note above), so it does
  // NOT survive a full page reload the way the persisted checkoutProgress.intakeComplete flag does.
  // If the enduser dismissed/left mid-session and comes back to a fresh mount with intakeComplete
  // still true, resuming straight to payment would silently treat every gated item as passed with
  // no real result behind it — violating the "always require a fresh submission" rule. Only resume
  // straight to payment when every gated medication actually has an in-memory result recorded;
  // otherwise fall back to re-running intake.
  const canResumeToPayment = cartMedications.every((med) => intakeStatusByProductId[med.stripeProductId] !== undefined);
  const resumeIntake = canResumeToPayment ? goToPayment : enterCheckout;

  const totalIntakeSteps = Math.max(cartMedications.length, 1) * 3;
  const currentIntakeStep = checkoutProgress.medIndex * 3 + checkoutProgress.questionIndex + 1;
  const currentMedName =
    cartMedications.length > 1
      ? t("your medications")
      : cartMedications[checkoutProgress.medIndex]?.title ?? t("your medication");

  const showResumeBanner =
    (checkoutProgress.intakeStarted || checkoutProgress.intakeComplete) &&
    !checkoutProgress.bannerDismissed &&
    (step === "catalog" || step === "cart") &&
    items.length > 0 &&
    // Guards against a stale intakeStarted/intakeComplete flag from a cart that previously had
    // gated items — e.g. a gated medication was removed from the cart after intake began. With no
    // gated medication currently in the cart, there is nothing to resume.
    cartMedications.length > 0;

  return {
    step,
    setStep,
    learnMoreProduct,
    setLearnMoreProduct,
    productsById,
    cartMedications,
    intakeStatusByProductId,
    setIntakeStatus,
    paymentCartRows,
    checkoutProgress,
    setCheckoutMedIndex,
    setCheckoutQuestionIndex,
    setCheckoutAnswers,
    goToShop,
    goToCart,
    enterCheckout,
    completeIntake,
    goToPayment,
    goToConfirmation,
    canResumeToPayment,
    resumeIntake,
    dismissResumeBanner,
    totalIntakeSteps,
    currentIntakeStep,
    currentMedName,
    showResumeBanner,
  };
};
